import React, { useState, useEffect } from "react";
import {
  TextField,
  // Button,
  Checkbox,
  FormControlLabel,
  Alert,
  Box,
} from "@mui/material";
import { AuthLayout } from "../../../shared/components/layout/AuthLayout";
import LoginIllustration from "../../../assets/illustrations/login.svg";
// import { ReactComponent as GoogleIcon } from "../../../assets/icons/google.svg";
// import { ReactComponent as AppleIcon } from "../../../assets/icons/apple.svg";
import {
  VALIDATOR_REQUIRE,
  VALIDATOR_EMAIL,
  validate,
} from "../../../shared/utils/validators";
import PasswordField from "../../../shared/components/ui/PasswordField";
import FormButton from "../../../shared/components/ui/FormButton";
import { ArrowBack } from "@mui/icons-material";

const REMEMBER_ME_STORAGE_KEY = "rememberedEmail";

const LoginView = ({
  loginFormState,
  loginInputHandler,
  handleTextInputChange,
  handleLogin,
  loginState,
  setLoginState,
  isActionLoading,
  textFieldStyle,
}) => {
  const [step, setStep] = useState("email");
  const [rememberMe, setRememberMe] = useState(false);

  // Load remember email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem(REMEMBER_ME_STORAGE_KEY);
    if (rememberedEmail) {
      const isValid = validate(rememberedEmail, [
        VALIDATOR_REQUIRE(),
        VALIDATOR_EMAIL(),
      ]);
      if (isValid) {
        loginInputHandler("email", rememberedEmail, isValid);
        setRememberMe(true);
      } else {
        localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
      }
    }
  }, [loginInputHandler]);

  const handleContinue = (e) => {
    e.preventDefault();
    if (loginFormState.inputs.email.isValid) {
      setStep("password");
    }
  };

  const handleBack = () => {
    setStep("email");
    loginInputHandler("password", "", false);
  };

  const handleRememberMeChange = (event) => {
    const isChecked = event.target.checked;
    setRememberMe(isChecked);

    if (!isChecked) {
      // Clear remembered email when unchecked
      localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
    }
  };

  const handleLoginWithRememberMe = async (e) => {
    e.preventDefault();

    if (rememberMe && loginFormState.inputs.email.value) {
      localStorage.setItem(
        REMEMBER_ME_STORAGE_KEY,
        loginFormState.inputs.email.value,
      );
    } else {
      localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
    }

    // Call the original handleLogin
    await handleLogin(e);
  };

  return (
    <AuthLayout
      title="Login"
      subtitle="Log In To Access Your Dashboard And Manage Your Account"
      illustration={LoginIllustration}
      headerContent={
        step === "password" && (
          <div className="mb-8 lg:mb-10">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 py-2 text-primary"
            >
              <ArrowBack fontSize="small" />
              <span className="text-sm font-medium">Back</span>
            </button>
          </div>
        )
      }
    >
      {loginState.message && (
        <Alert
          severity="error"
          className="mb-2"
          onClose={() => setLoginState({ state: "login", message: null })}
        >
          {loginState.message}
        </Alert>
      )}

      {step === "email" ? (
        <Box component="form" onSubmit={handleContinue}>
          <TextField
            label="Email"
            name="email"
            type="email"
            value={loginFormState.inputs.email.value}
            placeholder="john.doe@gmail.com"
            variant="outlined"
            fullWidth
            required
            sx={textFieldStyle}
            onChange={(e) =>
              handleTextInputChange(e, loginInputHandler, [
                VALIDATOR_REQUIRE(),
                VALIDATOR_EMAIL(),
              ])
            }
            focused
          />

          <FormButton
            type="submit"
            variant="contained"
            fullWidth
            size="medium"
            sx={{ backgroundColor: "primary.main" }}
            disabled={!loginFormState.inputs.email.isValid}
          >
            Continue
          </FormButton>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleLoginWithRememberMe}>
          <TextField
            label="Email"
            name="email"
            type="email"
            value={loginFormState.inputs.email.value}
            variant="outlined"
            fullWidth
            disabled
            sx={{
              ...textFieldStyle,
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "inherit",
              },
            }}
          />

          <PasswordField
            label="Password"
            name="password"
            formState={loginFormState}
            handler={loginInputHandler}
            validators={[VALIDATOR_REQUIRE()]}
            handleTextInputChange={handleTextInputChange}
            placeholder="Enter Your Password"
            variant="outlined"
            fullWidth
            required
            sx={textFieldStyle}
            focused
          />

          <div className="flex justify-between items-center mb-6">
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={handleRememberMeChange}
                />
              }
              label="Remember Me"
              className="text-primary text-sm"
            />
            <span
              onClick={() => setLoginState({ state: "forgot", message: null })}
              className="text-primary text-sm hover:underline cursor-pointer"
            >
              Forgot Password?
            </span>
          </div>

          <FormButton
            type="submit"
            variant="contained"
            fullWidth
            size="medium"
            sx={{ backgroundColor: "primary.main" }}
            loading={isActionLoading}
          >
            LOGIN
          </FormButton>
        </Box>
      )}

      {/* <div className="relative flex py-5 items-center justify-center">
        <div className="flex-grow border-t border-primary max-w-32"></div>
        <span className="flex-shrink-0 mx-6 text-primary text-xs font-semibold">
          Or Login With
        </span>
        <div className="flex-grow border-t border-primary max-w-32"></div>
      </div> */}

      {/* <div className="flex gap-4 justify-center">
        <Button
          variant="outlined"
          startIcon={<GoogleIcon />}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            width: "160px",
            height: "40px",
          }}
        >
          Google
        </Button>
        <Button
          variant="outlined"
          startIcon={<AppleIcon />}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            width: "160px",
            height: "40px",
          }}
        >
          Apple
        </Button>
      </div> */}

      <div className="text-center mt-8">
        <span className="text-sm">Don't Have An Account? </span>
        <span
          onClick={() => setLoginState({ state: "signup", message: null })}
          className="text-primary font-bold hover:underline cursor-pointer"
        >
          Sign Up
        </span>
      </div>
    </AuthLayout>
  );
};

export default LoginView;
